import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import api from "@/lib/api";
import { storeRef } from "@/lib/referral";
import {
  Sparkles, DollarSign, CalendarX, Share2, Tag, Globe2, KeyRound, UserPlus,
  Megaphone, Wallet, Video, Users, Briefcase, Network,
  ArrowRight, Check, ShieldCheck, Lock, Handshake,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1600582910964-5b7c109e6868?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHx0cm9waWNhbCUyMGJlYWNoJTIwcmVzb3J0JTIwYWVyaWFsJTIwdHVycXVvaXNlfGVufDB8fHx8MTc4NTc4NzU2NXww&ixlib=rb-4.1.0&q=85";
const PROMO_IMG = "https://images.unsplash.com/photo-1583528306385-8f29f27cb2d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxjb250ZW50JTIwY3JlYXRvciUyMGxhcHRvcCUyMHRyYXZlbCUyMGluZmx1ZW5jZXJ8ZW58MHx8fHwxNzg1Nzg3NTY1fDA&ixlib=rb-4.1.0&q=85";

const NAVY = "#0F2E5C";

const TOOL_DASHBOARD = "https://static.prod-images.emergentagent.com/jobs/0a6b177b-f7a9-4a1d-9bdc-483b983117a8/images/89544c3675597d26b5010666546d8da2034d9ceda4bbcf4ecb2b09db5f20a989.jpeg";
const TOOL_ASSETS = "https://static.prod-images.emergentagent.com/jobs/0a6b177b-f7a9-4a1d-9bdc-483b983117a8/images/dae42d096f01efca4e60ef664b3d6a367515d6c87881c4f53eb42fb8c303a2e9.jpeg";
const TOOL_LINK = "https://static.prod-images.emergentagent.com/jobs/0a6b177b-f7a9-4a1d-9bdc-483b983117a8/images/d372fa15d4eaf8b7411c0b9889e505d8c76e7f53626a5eebf9d174ffad48252b.jpeg";
const TOOL_SUPPORT = "https://static.prod-images.emergentagent.com/jobs/0a6b177b-f7a9-4a1d-9bdc-483b983117a8/images/44c868776cdf20ed9c7ddbe31969773d029db1873a0afe966d58c680e82e696f.jpeg";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

const money = (n) => "$" + Math.round(n).toLocaleString("en-US");

const Home = () => {
  const [pct, setPct] = useState(30);
  const [serverPct, setServerPct] = useState(30);
  const [price, setPrice] = useState(100);
  const [sold, setSold] = useState(10);

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) {
      storeRef(ref);
      api.post("/referral/click", { code: ref }).catch(() => {});
    }
    api.get("/public/commission").then(({ data }) => {
      if (data?.percent) { setServerPct(data.percent); setPct(data.percent); }
      if (data?.membership_fee) setPrice(data.membership_fee);
    }).catch(() => {});
  }, []);

  const monthly = sold * price * (pct / 100);
  const annual = monthly * 12;

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const benefits = [
    { icon: Sparkles, t: "Promote something people can use", d: "Travelers genuinely want deep savings on hotels and condos — you're sharing real value, not a gimmick." },
    { icon: DollarSign, t: "Earn on membership sales", d: `Collect a ${serverPct}% commission on every qualifying membership purchased through your link.` },
    { icon: CalendarX, t: "No travel booking required", d: "You never handle reservations or trips. Members book their own stays with their private PIN." },
    { icon: Share2, t: "Easy to share", d: "One unique link works across your posts, emails, bio and DMs — share it anywhere your audience is." },
  ];

  const promoting = [
    { icon: Tag, t: "Private member rates", d: "Savings of up to 35% off published rates — pricing that is never shown to the general public." },
    { icon: Globe2, t: "Worldwide options", d: "800,000+ hotels plus vacation condos across destinations around the globe." },
    { icon: KeyRound, t: "Simple member access", d: "Approved members unlock live inventory instantly with a private 10-digit PIN number." },
  ];

  const steps = [
    { n: "01", icon: UserPlus, t: "Join", d: "Apply in minutes. Once approved, you receive your unique affiliate referral link." },
    { n: "02", icon: Megaphone, t: "Share", d: "Promote the savings program to your audience, community or network with your link." },
    { n: "03", icon: Wallet, t: "Earn", d: `Earn a ${serverPct}% commission on every qualifying membership purchase made through your link.` },
  ];

  const audiences = [
    { icon: Video, t: "Content creators & influencers", d: "Travel, lifestyle and deal-focused creators with an engaged following." },
    { icon: Users, t: "Community leaders & organizers", d: "Group admins, clubs and organizers who share resources with members." },
    { icon: Briefcase, t: "Business owners & entrepreneurs", d: "Add a travel-savings perk or a new revenue stream to your business." },
    { icon: Network, t: "Anyone with a network", d: "Friends, family and colleagues — if you know travelers, you can earn." },
  ];

  const tools = [
    { img: TOOL_DASHBOARD, t: "Personal Affiliate Dashboard", d: "See referral activity, account information, and available program details in one private dashboard." },
    { img: TOOL_ASSETS, t: "Ready-to-Share Marketing Assets", d: "Use approved banners, images, and messaging instead of starting every promotion from scratch." },
    { img: TOOL_LINK, t: "Your Unique Referral Link", d: "Share one personal link so eligible membership purchases can be connected to your account." },
    { img: TOOL_SUPPORT, t: "Guides and Program Support", d: "Access practical guidance and approved materials that help you explain the membership clearly." },
  ];

  const faqs = [
    { q: "Do I need to be a travel agent?", a: "No. There are no licenses, certifications or travel-industry experience required. If you can share a link, you can be an affiliate." },
    { q: "How do I earn?", a: `You earn a ${serverPct}% commission on every qualifying membership purchased through your unique referral link. Earnings are credited to your affiliate balance.` },
    { q: "What am I promoting?", a: "A private hotel & condo savings membership. Members unlock rates of up to 35% off published prices — pricing that is not available to the general public." },
    { q: "Do I handle hotel reservations?", a: "No. You never book or manage any travel. Members book their own stays directly using their private PIN number." },
    { q: "How are my referrals tracked?", a: "Every affiliate gets a unique referral link. When someone signs up through it, they're automatically attributed to you and tracked in your dashboard." },
  ];

  const stats = [
    { icon: Tag, h: "Up to 35% off", d: "Hotels, condos & vacation rentals" },
    { icon: Globe2, h: "Global inventory", d: "Options in destinations worldwide" },
    { icon: Lock, h: "Private pricing", d: "Rates not open to the public" },
  ];

  return (
    <div className="relative min-h-screen bg-[var(--ir-bg)] text-[var(--ir-text)] overflow-x-hidden" data-testid="home-page">
      {/* STICKY HEADER */}
      <header className="fixed top-0 inset-x-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[var(--ir-border)]">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between gap-4">
          <Link to="/" data-testid="header-logo"><Logo size="md" /></Link>
          <nav className="hidden lg:flex items-center gap-8 text-sm font-medium">
            <a href="#why" onClick={scrollTo("why")} className="hover:text-[var(--ir-orange)] transition-colors" data-testid="nav-why">Why join</a>
            <a href="#how" onClick={scrollTo("how")} className="hover:text-[var(--ir-orange)] transition-colors" data-testid="nav-how">How it works</a>
            <a href="#faq" onClick={scrollTo("faq")} className="hover:text-[var(--ir-orange)] transition-colors" data-testid="nav-faq">FAQ</a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button asChild variant="ghost" className="text-[var(--ir-text)] hover:text-[var(--ir-orange)] hover:bg-[var(--ir-surface2)]" data-testid="header-login-btn">
              <Link to="/login">Affiliate login</Link>
            </Button>
            <Button asChild className="bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white font-semibold rounded-none px-5" data-testid="header-apply-btn">
              <Link to="/register">Apply to join</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center pt-20">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Aerial view of a turquoise beach resort" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(100deg, ${NAVY}F2 0%, ${NAVY}D9 42%, ${NAVY}66 100%)` }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full py-24">
          <motion.span variants={fadeUp} initial="hidden" animate="show" className="inline-flex items-center gap-2 px-4 py-1.5 border border-white/30 rounded-full mb-8 bg-white/10 text-xs tracking-[0.28em] uppercase text-white/90 font-semibold" data-testid="hero-eyebrow">
            <Handshake className="w-3.5 h-3.5 text-[var(--ir-orange)]" /> Affiliate Program
          </motion.span>
          <motion.h1 variants={fadeUp} custom={1} initial="hidden" animate="show" className="font-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] max-w-4xl tracking-tight text-white" data-testid="hero-headline">
            Turn your audience into <span className="italic text-[var(--ir-orange)]">travel savings income</span>.
          </motion.h1>
          <motion.p variants={fadeUp} custom={2} initial="hidden" animate="show" className="mt-7 text-lg sm:text-xl text-white/85 max-w-2xl leading-relaxed">
            Promote a private hotel & condo savings membership and earn a {serverPct}% commission on every qualifying sign-up. No travel bookings, no inventory, no experience required.
          </motion.p>
          <motion.div variants={fadeUp} custom={3} initial="hidden" animate="show" className="mt-10 flex flex-wrap items-center gap-4">
            <Button asChild className="bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white font-semibold rounded-none px-8 text-base h-12" data-testid="hero-apply-btn">
              <Link to="/register">Apply to join <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
            <Button asChild variant="outline" className="border-white/40 bg-white/10 text-white hover:bg-white/20 rounded-none h-12 px-8 text-base" data-testid="hero-login-btn">
              <Link to="/login">Affiliate login</Link>
            </Button>
          </motion.div>
          <motion.p variants={fadeUp} custom={4} initial="hidden" animate="show" className="mt-5 text-sm text-white/70 flex items-center gap-2">
            <Check className="w-4 h-4 text-[var(--ir-orange)]" /> Free to apply · No travel bookings required
          </motion.p>
        </div>
      </section>

      {/* STAT BADGES */}
      <section className="relative z-20 max-w-7xl mx-auto px-6 -mt-14">
        <div className="grid sm:grid-cols-3 gap-5">
          {stats.map((s, i) => (
            <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="bg-white border border-[var(--ir-border)] rounded-xl p-6 shadow-sm flex items-start gap-4" data-testid={`stat-${i + 1}`}>
              <div className="shrink-0 w-11 h-11 flex items-center justify-center rounded-lg bg-[var(--ir-orange)]/10 text-[var(--ir-orange)]"><s.icon className="w-5 h-5" /></div>
              <div>
                <div className="font-display text-xl text-[var(--ir-text)]">{s.h}</div>
                <div className="text-sm text-[var(--ir-muted)] mt-1">{s.d}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHY BECOME AN AFFILIATE */}
      <section id="why" className="max-w-7xl mx-auto px-6 py-28 scroll-mt-24" data-testid="why-section">
        <div className="max-w-2xl mb-14">
          <span className="text-xs tracking-[0.3em] uppercase text-[var(--ir-orange)] font-semibold">Why join</span>
          <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight">Why become an affiliate</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {benefits.map((b, i) => (
            <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="relative bg-white border border-[var(--ir-border)] rounded-xl p-8 flex items-start gap-5" data-testid={`benefit-${i + 1}`}>
              <span className="absolute top-6 right-7 font-display text-4xl text-[var(--ir-border)]">{`0${i + 1}`}</span>
              <div className="shrink-0 w-12 h-12 flex items-center justify-center rounded-lg text-white" style={{ background: NAVY }}><b.icon className="w-5 h-5" /></div>
              <div>
                <h3 className="font-display text-xl text-[var(--ir-text)]">{b.t}</h3>
                <p className="text-[var(--ir-muted)] mt-2 leading-relaxed pr-6">{b.d}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHAT YOU'LL BE PROMOTING */}
      <section className="relative" style={{ background: NAVY }} data-testid="promoting-section">
        <div className="max-w-7xl mx-auto px-6 py-28 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-[var(--ir-orange)] font-semibold">The offer</span>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight text-white">What you'll be promoting</h2>
            <div className="mt-10 space-y-8">
              {promoting.map((p, i) => (
                <div key={i} className="flex items-start gap-5" data-testid={`promoting-${i + 1}`}>
                  <div className="shrink-0 w-11 h-11 flex items-center justify-center rounded-lg bg-[var(--ir-orange)] text-white"><p.icon className="w-5 h-5" /></div>
                  <div>
                    <h3 className="font-display text-xl text-white">{p.t}</h3>
                    <p className="text-white/75 mt-1.5 leading-relaxed">{p.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img src={PROMO_IMG} alt="Creator sharing travel deals on a laptop" className="rounded-xl w-full h-[460px] object-cover" />
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="max-w-7xl mx-auto px-6 py-28 scroll-mt-24" data-testid="how-section">
        <div className="max-w-2xl mb-14">
          <span className="text-xs tracking-[0.3em] uppercase text-[var(--ir-orange)] font-semibold">How it works</span>
          <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight">Earning in three simple steps</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="bg-white border border-[var(--ir-border)] rounded-xl p-8" data-testid={`step-${i + 1}`}>
              <div className="flex items-center justify-between">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg text-white" style={{ background: NAVY }}><s.icon className="w-5 h-5" /></div>
                <span className="font-display text-3xl text-[var(--ir-orange)]">{s.n}</span>
              </div>
              <h3 className="font-display text-2xl mt-6 text-[var(--ir-text)]">{s.t}</h3>
              <p className="text-[var(--ir-muted)] mt-2 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* WHO CAN BECOME AN AFFILIATE */}
      <section className="bg-[var(--ir-surface2)] border-y border-[var(--ir-border)]" data-testid="audience-section">
        <div className="max-w-7xl mx-auto px-6 py-28">
          <div className="max-w-2xl mb-14">
            <span className="text-xs tracking-[0.3em] uppercase text-[var(--ir-orange)] font-semibold">Who it's for</span>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight">Who can become an affiliate</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {audiences.map((a, i) => (
              <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="bg-white border border-[var(--ir-border)] rounded-xl p-7" data-testid={`audience-${i + 1}`}>
                <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-[var(--ir-orange)]/10 text-[var(--ir-orange)]"><a.icon className="w-5 h-5" /></div>
                <h3 className="font-display text-lg mt-5 text-[var(--ir-text)]">{a.t}</h3>
                <p className="text-[var(--ir-muted)] text-sm mt-2 leading-relaxed">{a.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TOOLS FOR AFFILIATES */}
      <section className="max-w-7xl mx-auto px-6 py-28" data-testid="tools-section">
        <div className="max-w-2xl mb-14">
          <span className="text-xs tracking-[0.3em] uppercase text-[var(--ir-orange)] font-semibold">Your toolkit</span>
          <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight">Tools for affiliates</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {tools.map((t, i) => (
            <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="rounded-xl overflow-hidden" style={{ background: NAVY }} data-testid={`tool-${i + 1}`}>
              <div className="p-4 pb-0">
                <img src={t.img} alt={t.t} className="w-full h-60 object-cover object-center rounded-lg" />
              </div>
              <div className="h-1 bg-[var(--ir-orange)] mx-8 mt-6" />
              <div className="p-8 pt-5">
                <h3 className="font-display text-2xl text-white">{t.t}</h3>
                <p className="text-white/70 mt-3 leading-relaxed">{t.d}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* EARNINGS CALCULATOR */}
      <section className="bg-[var(--ir-surface2)] border-y border-[var(--ir-border)]" data-testid="calculator-section">
        <div className="max-w-6xl mx-auto px-6 py-28">
          <div className="max-w-2xl mb-12">
            <span className="text-xs tracking-[0.3em] uppercase text-[var(--ir-orange)] font-semibold">Estimate your income</span>
            <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight">Earnings calculator</h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-8 items-stretch">
            <div className="bg-white border border-[var(--ir-border)] rounded-xl p-8 space-y-9">
              <div>
                <div className="flex justify-between text-sm font-medium mb-3"><span>Memberships sold / month</span><span className="text-[var(--ir-orange)] font-semibold" data-testid="calc-sold-value">{sold}</span></div>
                <Slider min={1} max={100} step={1} value={[sold]} onValueChange={(v) => setSold(v[0])} data-testid="calc-sold-slider" />
              </div>
              <div>
                <div className="flex justify-between text-sm font-medium mb-3"><span>Membership price</span><span className="text-[var(--ir-orange)] font-semibold" data-testid="calc-price-value">{money(price)}</span></div>
                <Slider min={25} max={500} step={5} value={[price]} onValueChange={(v) => setPrice(v[0])} data-testid="calc-price-slider" />
              </div>
              <div>
                <div className="flex justify-between text-sm font-medium mb-3"><span>Commission percentage</span><span className="text-[var(--ir-orange)] font-semibold" data-testid="calc-pct-value">{pct}%</span></div>
                <Slider min={5} max={60} step={1} value={[pct]} onValueChange={(v) => setPct(v[0])} data-testid="calc-pct-slider" />
              </div>
            </div>
            <div className="rounded-xl p-8 flex flex-col justify-center text-white" style={{ background: NAVY }}>
              <div className="text-xs tracking-[0.28em] uppercase text-white/70 font-semibold">Estimated earnings</div>
              <div className="mt-8">
                <div className="text-sm text-white/70">Per month</div>
                <div className="font-display text-5xl sm:text-6xl text-[var(--ir-orange)] mt-1" data-testid="calc-monthly">{money(monthly)}</div>
              </div>
              <div className="mt-8 pt-8 border-t border-white/15">
                <div className="text-sm text-white/70">Per year</div>
                <div className="font-display text-4xl sm:text-5xl text-white mt-1" data-testid="calc-annual">{money(annual)}</div>
              </div>
            </div>
          </div>
          <p className="text-xs text-[var(--ir-muted)] mt-6 leading-relaxed max-w-4xl" data-testid="calc-disclaimer">
            For illustration only. Actual earnings depend on qualifying membership sales, the commission rate in effect, refunds, and final program terms. Earnings are not guaranteed.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-6 py-28 scroll-mt-24" data-testid="faq-section">
        <div className="mb-12 text-center">
          <span className="text-xs tracking-[0.3em] uppercase text-[var(--ir-orange)] font-semibold">Questions</span>
          <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight">Frequently asked</h2>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-[var(--ir-border)]" data-testid={`faq-${i + 1}`}>
              <AccordionTrigger className="text-left font-display text-lg text-[var(--ir-text)] hover:no-underline hover:text-[var(--ir-orange)]">{f.q}</AccordionTrigger>
              <AccordionContent className="text-[var(--ir-muted)] leading-relaxed text-base">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CLOSING CTA */}
      <section className="relative overflow-hidden" style={{ background: NAVY }} data-testid="closing-cta">
        <div className="absolute inset-0" style={{ background: `radial-gradient(circle at 80% 20%, rgba(255,122,24,0.22), transparent 55%)` }} />
        <div className="relative max-w-4xl mx-auto px-6 py-28 text-center">
          <ShieldCheck className="w-10 h-10 text-[var(--ir-orange)] mx-auto" />
          <h2 className="font-display text-4xl sm:text-5xl mt-6 leading-tight text-white">Start earning with every <span className="italic text-[var(--ir-orange)]">member you refer</span>.</h2>
          <p className="mt-5 text-lg text-white/80 max-w-2xl mx-auto">Apply free in minutes, get your referral link, and start sharing a savings program travelers actually want.</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Button asChild className="bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white font-semibold rounded-none h-12 px-8 text-base" data-testid="cta-apply-btn">
              <Link to="/register">Apply to join <ArrowRight className="w-4 h-4 ml-2" /></Link>
            </Button>
            <Button asChild variant="outline" className="border-white/40 bg-white/10 text-white hover:bg-white/20 rounded-none h-12 px-8 text-base" data-testid="cta-login-btn">
              <Link to="/login">Affiliate login</Link>
            </Button>
          </div>
          <p className="mt-5 text-sm text-white/60">Free to apply · No travel bookings required</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-white border-t border-[var(--ir-border)]" data-testid="home-footer">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-10">
          <div>
            <Logo size="md" />
            <p className="text-[var(--ir-muted)] text-sm mt-4 max-w-xs leading-relaxed">Hotel Savings Program powered by International Vacations, Inc.</p>
          </div>
          <div className="flex flex-col gap-3 text-sm">
            <span className="text-xs tracking-[0.25em] uppercase text-[var(--ir-orange)] font-semibold">Program</span>
            <a href="#why" onClick={scrollTo("why")} className="text-[var(--ir-text)] hover:text-[var(--ir-orange)] transition-colors">Why join</a>
            <a href="#how" onClick={scrollTo("how")} className="text-[var(--ir-text)] hover:text-[var(--ir-orange)] transition-colors">How it works</a>
            <a href="#faq" onClick={scrollTo("faq")} className="text-[var(--ir-text)] hover:text-[var(--ir-orange)] transition-colors">FAQ</a>
          </div>
          <div className="flex flex-col gap-3 text-sm">
            <span className="text-xs tracking-[0.25em] uppercase text-[var(--ir-orange)] font-semibold">Links</span>
            <Link to="/register" className="text-[var(--ir-text)] hover:text-[var(--ir-orange)] transition-colors">Apply to join</Link>
            <Link to="/login" className="text-[var(--ir-text)] hover:text-[var(--ir-orange)] transition-colors">Affiliate login</Link>
            <Link to="/affiliates" className="text-[var(--ir-text)] hover:text-[var(--ir-orange)] transition-colors">Member booking portal</Link>
            <Link to="/terms" className="text-[var(--ir-text)] hover:text-[var(--ir-orange)] transition-colors">Terms &amp; Card Usage</Link>
          </div>
        </div>
        <div className="border-t border-[var(--ir-border)]">
          <div className="max-w-7xl mx-auto px-6 py-6 text-[var(--ir-muted)] text-sm">© {new Date().getFullYear()} Image Reservations. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
